"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import React from "react"
import { useInView } from "react-intersection-observer"
import { getNonUmbracoUrl } from "utils"
import { transitionDelayTime } from ".."
import CommunityTexts from "../common/bit/CommunityTexts"
import { useIsDesktop } from "../common/bit/MediaQueries"
import BlockWrapper from "../common/BlockWrapper"
import { mediaQueries, sizes } from "../theme/theme"
import { useTransitionUpStyles } from "../transitions/useTransitionUpStyles"
import { useInViewConfig } from "../transitions/utils"
import VideoContainer from "./VideoContainer"

const StyledBlockWrapper = styled(BlockWrapper)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 60px;
  width: 100%;
  padding-top: 120px;
  z-index: 2 !important;
  ${mediaQueries.tabletDown} {
    flex-direction: column;
    padding-top: 80px;
  }
`

const ImageWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${mediaQueries.tabletDown} {
    margin-bottom: ${sizes.baseTriple};
    order: 2;
  }
  ${mediaQueries.desktopM} {
    width: 50%;
  }
`

const MainImageContainer = styled(Box)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  width: 100%;
  height: auto;
  img {
    object-fit: contain;
    border-radius: ${sizes.borderRadius};
  }
`

const ContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 2;
  ${mediaQueries.mobile} {
    max-width: 100%;
  }
  ${mediaQueries.tablet} {
    max-width: 100%;
  }
  ${mediaQueries.desktopM} {
    width: 50%;
    height: 100%;
  }
`
const StyledCommunityTexts = styled(CommunityTexts)`
  ${mediaQueries.desktopM} {
    height: 100%;
  }
`
const StyledRedText = styled(Typography)`
  margin-top: ${sizes.baseTriple};
  ${mediaQueries.desktopM} {
    margin-top: ${sizes.baseSepta};
  }
`

type ArticleBannerClientProps = {
  data: {
    title?: any
    description?: any
    image?: any
    video?: any
    fetchedItems?: any
  }
  id?: string
}

const ArticleBannerClient: React.FC<ArticleBannerClientProps> = ({
  data,
  id,
}) => {
  const { description, fetchedItems } = data
  const { title, image, video, link, category, details } = fetchedItems
  const isDesktop = useIsDesktop()
  const { ref: contentRef, inView: contentInView } = useInView(useInViewConfig)
  const { ref: imageRef, inView: imageInView } = useInView(useInViewConfig)
  const contentTransitionStyles = useTransitionUpStyles(
    contentInView,
    transitionDelayTime,
  )
  const imageTransitionStyles = useTransitionUpStyles(
    imageInView,
    transitionDelayTime,
  )
  const t = useTranslations("ArticleBanner")

  // Format the details string according to the required pattern
  const formattedDate = details?.date?.dateTime
    ? new Date(details.date.dateTime)
        .toLocaleDateString("he-IL", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
        .replace(/\./g, ".")
    : ""

  const detailsString = `${t("published")}: ${formattedDate} | ${t("by")}: ${details?.author?.value || ""} | ${details?.readingTime?.value || ""} ${t("readingTime")}`

  return (
    <StyledBlockWrapper id={id}>
      <ImageWrapper ref={imageRef} style={{ ...imageTransitionStyles }}>
        <MainImageContainer>
          {image?.url ? (
            <Image
              src={image?.url}
              alt={image?.altText || title?.value || t("imageAlt")}
              width={isDesktop ? 750 : 380}
              height={isDesktop ? 560 : 248}
              priority
            />
          ) : (
            <VideoContainer
              url={getNonUmbracoUrl(video?.mediaItems?.[0]?.url)}
              title={title?.value}
            />
          )}
        </MainImageContainer>
        {details && !isDesktop && (
          <StyledRedText variant="body2">{detailsString}</StyledRedText>
        )}
      </ImageWrapper>
      <ContentWrapper ref={contentRef} style={{ ...contentTransitionStyles }}>
        <StyledCommunityTexts
          titleComponent={"h1"}
          data={{ text: description?.value }}
        >
          {title}
        </StyledCommunityTexts>
        {details && isDesktop && (
          <StyledRedText variant="body2">{detailsString}</StyledRedText>
        )}
      </ContentWrapper>
    </StyledBlockWrapper>
  )
}

export default ArticleBannerClient
